import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarHeart, Sparkles } from 'lucide-react';
import { SCShell, SCHeading, goldBtn, RequireAuth } from './sc/SCShared';
import { scActivate, scGetProfile } from '../api';

const Inner = () => {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [dob, setDob] = useState('');
  const [confirm18, setConfirm18] = useState(false);
  const [visibility, setVisibility] = useState('public');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    scGetProfile().then((p) => { if (p && p.age_verification_status === 'verified') navigate('/sacred-connections/profile'); }).catch(() => {});
  }, [navigate]);

  const submit = async () => {
    if (!confirm18) return toast.error('Please confirm you are 18 or older.');
    if (!dob) return toast.error('Please enter your date of birth.');
    setSaving(true);
    try {
      await scActivate({ display_name: displayName, date_of_birth: dob, confirm_18: confirm18, profile_visibility: visibility });
      toast.success('Welcome to Sacred Connections.');
      navigate('/sacred-connections/profile');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not activate. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <SCShell>
      <SCHeading eyebrow="Activate" title="Create your dating profile" sub="Sacred Connections is for adults 18+. Confirm a few details to begin — your date of birth stays private and only your age is shown." />
      <div className="max-w-lg rounded-2xl p-7 border border-[#8b5cf6]/25 bg-white/[0.03] backdrop-blur-md space-y-5">
        <div>
          <label className="font-jost text-xs uppercase tracking-widest text-[#f5d786] mb-2 block">Display name</label>
          <input data-testid="sc-onb-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="How you'll appear" className="w-full bg-black/30 border border-[#8b5cf6]/30 rounded-xl px-4 py-3 text-[#e9d8ff] focus:outline-none focus:border-[#d946ef]" />
        </div>
        <div>
          <label className="font-jost text-xs uppercase tracking-widest text-[#f5d786] mb-2 block flex items-center gap-2"><CalendarHeart className="h-4 w-4" /> Date of birth (private)</label>
          <input data-testid="sc-onb-dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="w-full bg-black/30 border border-[#8b5cf6]/30 rounded-xl px-4 py-3 text-[#e9d8ff] focus:outline-none focus:border-[#d946ef]" />
        </div>
        <div>
          <label className="font-jost text-xs uppercase tracking-widest text-[#f5d786] mb-2 block">Profile visibility</label>
          <select data-testid="sc-onb-visibility" value={visibility} onChange={(e) => setVisibility(e.target.value)} className="w-full bg-black/30 border border-[#8b5cf6]/30 rounded-xl px-4 py-3 text-[#e9d8ff] focus:outline-none focus:border-[#d946ef]">
            <option value="public">Public to Sacred Connections users</option>
            <option value="matches_only">Visible only to matches</option>
            <option value="incognito">Incognito (discoverable when you like someone)</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
        <label className="flex items-start gap-3 cursor-pointer" data-testid="sc-onb-confirm">
          <input type="checkbox" checked={confirm18} onChange={(e) => setConfirm18(e.target.checked)} className="mt-1 h-4 w-4 accent-[#d946ef]" />
          <span className="font-jost text-sm text-[#e9d8ff]/75">I confirm that I am 18 years of age or older and agree to use Sacred Connections respectfully.</span>
        </label>
        <button data-testid="sc-onb-submit" disabled={saving} onClick={submit} className={goldBtn}><Sparkles className="h-4 w-4" /> {saving ? 'Activating…' : 'Activate Sacred Connections'}</button>
      </div>
    </SCShell>
  );
};

const SCOnboarding = () => <RequireAuth><Inner /></RequireAuth>;
export default SCOnboarding;
